import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Bell, AlertTriangle, Check, Package, Store, Clock } from 'lucide-react';
import { apiFetch } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await apiFetch('/api/alerts', {
      });
      if (response.ok) {
        setAlerts(await response.json());
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (alertId) => {
    try {
      const response = await apiFetch(`/api/alerts/${alertId}/read`, {
        method: 'PATCH',
      });
      if (response.ok) {
        setAlerts((prev) =>
          prev.map((a) => (a.alert_id === alertId ? { ...a, is_read: true } : a))
        );
      }
    } catch (error) {
      console.error('Error marking alert as read:', error);
    }
  };

  const filteredAlerts = alerts.filter((alert) => {
    if (filter === 'unread') return !alert.is_read;
    if (filter === 'read') return alert.is_read;
    return true;
  });

  const unreadCount = alerts.filter((a) => !a.is_read).length;

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300';
      case 'medium':
        return 'bg-[#f59e0b]/10 text-[#f59e0b]';
      default:
        return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diff = now - date;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const days = Math.floor(hours / 24);

      if (days > 0) return `${days}d ago`;
      if (hours > 0) return `${hours}h ago`;
      return 'Just now';
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-6 w-48 mb-2" />
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="alerts-page">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">Alerts</h1>
          <p className="text-muted-foreground mt-1">
            Automated notifications for unusual patterns
          </p>
        </div>
        {unreadCount > 0 && (
          <Badge className="bg-destructive text-destructive-foreground">
            {unreadCount} unread
          </Badge>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
          className={filter === 'all' ? 'bg-[#2eb4ac] hover:bg-[#25908a]' : ''}
          data-testid="filter-all-btn"
        >
          All ({alerts.length})
        </Button>
        <Button
          variant={filter === 'unread' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('unread')}
          className={filter === 'unread' ? 'bg-[#2eb4ac] hover:bg-[#25908a]' : ''}
          data-testid="filter-unread-btn"
        >
          Unread ({unreadCount})
        </Button>
        <Button
          variant={filter === 'read' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('read')}
          className={filter === 'read' ? 'bg-[#2eb4ac] hover:bg-[#25908a]' : ''}
          data-testid="filter-read-btn"
        >
          Read ({alerts.length - unreadCount})
        </Button>
      </div>

      {/* Alerts list */}
      {filteredAlerts.length > 0 ? (
        <div className="space-y-4">
          {filteredAlerts.map((alert, idx) => (
            <Card
              key={alert.alert_id}
              className={`card-hover stagger-item ${
                !alert.is_read ? 'border-l-4 border-l-[#2eb4ac]' : ''
              }`}
              data-testid={`alert-card-${idx}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        alert.severity === 'high'
                          ? 'bg-red-100 dark:bg-red-900/30'
                          : 'bg-[#f59e0b]/10'
                      }`}
                    >
                      <AlertTriangle
                        className={`w-5 h-5 ${
                          alert.severity === 'high' ? 'text-red-600' : 'text-[#f59e0b]'
                        }`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-medium">{alert.title}</h3>
                        <Badge className={getSeverityColor(alert.severity)}>
                          {alert.severity}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {alert.description}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {alert.product && (
                          <span className="flex items-center gap-1">
                            <Package className="w-3 h-3" />
                            {alert.product}
                          </span>
                        )}
                        {alert.platform && (
                          <span className="flex items-center gap-1">
                            <Store className="w-3 h-3" />
                            {alert.platform}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(alert.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {!alert.is_read && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => markAsRead(alert.alert_id)}
                      data-testid={`mark-read-btn-${idx}`}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Mark read
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Alerts</h3>
            <p className="text-muted-foreground">
              {filter !== 'all'
                ? 'No alerts match your filter.'
                : 'No alerts yet. Upload and analyze reviews to start monitoring.'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
